import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { StarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  deleteFetcherKeyed,
  optimisticPrd,
  optimisticPrds,
  putFetcher,
  useAuthStore,
} from "@/lib/swr";
import type { Prd } from "@/types/domain";

// Self-service watch toggle: followers receive the PRD's Feishu activity
// alongside owner and members (see todos/notify-feishu.md). Shared by the
// detail header and list cards — the server echoes the updated follower list,
// which is fanned out to both the detail atom and the /prds list caches.
export const FollowButton = ({
  prd,
  compact,
  autoFollowParam,
  className,
}: {
  prd: Prd;
  /** Icon-only rendering for list cards. */
  compact?: boolean;
  /** Honor ?follow=1 (Feishu notification 关注 entry) — detail page only. */
  autoFollowParam?: boolean;
  className?: string;
}) => {
  const me = useAuthStore((store) => store.auth?.user);
  const { toast } = useToast();
  const following = !!me?.id && (prd.followerIds ?? []).includes(me.id);

  // The server echoes the updated follower id list.
  const apply = (res: { followerIds?: string[] }) => {
    if (!Array.isArray(res?.followerIds)) return;
    optimisticPrd(prd.id, { followerIds: res.followerIds });
    optimisticPrds(prd.id, { followerIds: res.followerIds });
  };
  const { trigger: follow, isMutating: following1 } = useSWRMutation(
    `/prds/${prd.id}/follow`,
    putFetcher,
    { onSuccess: apply }
  );
  const { trigger: unfollow, isMutating: following2 } = useSWRMutation(
    `/prds/${prd.id}/follow#del`,
    deleteFetcherKeyed,
    { onSuccess: apply }
  );
  const busy = following1 || following2;

  // ?follow=1 lands here from the notification's 关注 button: follow once
  // (idempotent server-side), toast, and drop the param from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoTried = useRef(false);
  const wantsAutoFollow =
    !!autoFollowParam && searchParams.get("follow") === "1";
  useEffect(() => {
    if (!wantsAutoFollow || autoTried.current || !me) return;
    autoTried.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete("follow");
    setSearchParams(next, { replace: true });
    if (following) {
      toast({ description: "You've already starred this PRD." });
    } else {
      follow({}).then(() =>
        toast({ description: "Starred — you'll get this PRD's updates." })
      );
    }
  }, [wantsAutoFollow, me, following, follow, searchParams, setSearchParams, toast]);

  // Owner/creator already sit in the notify group (creator ∪ owner ∪
  // members ∪ followers) — a Follow toggle on one's own PRD is noise
  // (owner 2026-08-18). Hooks above still run: ?follow=1 stays harmless.
  const isMine =
    !!me?.id && (prd.ownerId === me.id || prd.creatorId === me.id);
  if (isMine) return null;

  if (compact) {
    return (
      <Button
        variant={following ? "secondary" : "ghost"}
        size="icon"
        title={following ? "Unstar" : "Star"}
        aria-label={following ? "Unstar" : "Star"}
        disabled={busy}
        onClick={() => (following ? unfollow() : follow({}))}
        className={cn("h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground", className)}
      >
        <StarIcon className={cn("h-3.5 w-3.5", following && "fill-current")} />
      </Button>
    );
  }

  return (
    <Button
      variant={following ? "secondary" : "outline"}
      size="sm"
      onClick={() => (following ? unfollow() : follow({}))}
      disabled={busy}
      className={className}
    >
      <StarIcon className={cn("h-4 w-4", following && "fill-current")} />
      {following ? "Starred" : "Star"}
    </Button>
  );
};
